export enum MasterReport {
  TR = "TR",
  DR = "DR",
  PR = "PR",
  IR = "IR",
}

export enum SubReport {
  TR_B1 = "TR_B1",
  TR_B2 = "TR_B2",
  TR_B3 = "TR_B3",
  TR_J1 = "TR_J1",
  TR_J2 = "TR_J2",
  TR_J3 = "TR_J3",
  TR_J4 = "TR_J4",
  DR_D1 = "DR_D1",
  DR_D2 = "DR_D2",
  PR_P1 = "PR_P1",
  IR_A1 = "IR_A1",
  IR_M1 = "IR_M1",
}

export type ReportType = MasterReport | SubReport;
export const ReportType = {...MasterReport, ...SubReport}; // eslint-disable-line

export interface ReportGroup {
  id: MasterReport,
  label: string
}

const reportGroups: Record<MasterReport, ReportGroup> = {
  TR: {id: MasterReport.TR, label: "Title Reports"},
  DR: {id: MasterReport.DR, label: "Database Reports"},
  PR: {id: MasterReport.PR, label: "Platform Reports"},
  IR: {id: MasterReport.IR, label: "Item Reports"}
};

export interface ReportDefinition {
  label: string;
  description: string;
  value: ReportType;
  group: ReportGroup;
}

export const allReports: ReportDefinition[] = [
  {
    label: "(TR) Title Report",
    value: ReportType.TR,
    description: "A customizable report detailing activity at the title level (journal, book, etc.) that allows the user to apply filters and select other configuration options.",
    group: reportGroups.TR
  },
  {
    label: "(TR_B1) Book Requests (Controlled)",
    value: ReportType.TR_B1,
    description: "Reports on full-text activity for books, excluding Gold Open Access content, as Total_Item_Requests and Unique_Title_Requests. The Unique_Title_Requests provides comparable usage across book platforms. The Total_Item_Requests shows overall activity; however, numbers between sites will vary significantly based on how the content is delivered (e.g. delivered as a complete book or by chapter).",
    group: reportGroups.TR
  },
  {
    label: "(TR_B2) Book Access Denied",
    value: ReportType.TR_B2,
    description: "Reports on Access Denied activity for books where users were denied access because simultaneous-use licenses were exceeded or their institution did not have a license for the book.",
    group: reportGroups.TR
  },
  {
    label: "(TR_B3) Book Usage by Access Type",
    value: ReportType.TR_B3,
    description: "Reports on book usage showing all applicable Metric_Types broken down by Access_Type.",
    group: reportGroups.TR
  },
  {
    label: "(TR_J1) Journal Requests (Controlled)",
    value: ReportType.TR_J1,
    description: "Reports on usage of journal content, excluding Gold Open Access content, as Total_Item_Requests and Unique_Item_Requests. The Unique_Item_Requests provides comparable usage across journal platforms by reducing the inflationary effect that occurs when an HTML full text automatically displays and the user then accesses the PDF version. The Total_Item_Requests shows overall activity.",
    group: reportGroups.TR
  },
  {
    label: "(TR_J2) Journal Access Denied",
    value: ReportType.TR_J2,
    description: "Reports on Access Denied activity for journal content where users were denied access because simultaneous-use licenses were exceeded or their institution did not have a license for the title.",
    group: reportGroups.TR
  },
  {
    label: "(TR_J3) Journal Usage by Access Type",
    value: ReportType.TR_J3,
    description: "Reports on usage of journal content for all Metric_Types broken down by Access_Type.",
    group: reportGroups.TR
  },
  {
    label: "(TR_J4) Journal Requests by YOP (Controlled)",
    value: ReportType.TR_J4,
    description: "Breaks down the usage of journal content, excluding Gold Open Access content, by year of publication (YOP), providing counts for the Metric_Types Total_Item_Requests and Unique_Item_Requests. Provides the details necessary to analyze usage of content in backfiles or covered by perpetual access agreements. Note that COUNTER reports do not provide access model or perpetual access rights details.",
    group: reportGroups.TR
  },
  {
    label: "(DR) Database Report",
    value: ReportType.DR,
    description: "A customizable report detailing activity by database that allows the user to apply filters and select other configuration options.",
    group: reportGroups.DR
  },
  {
    label: "(DR_D1) Database Search and Item Usage",
    value: ReportType.DR_D1,
    description: "Reports on key Searches, Investigations and Requests metrics needed to evaluate a database.",
    group: reportGroups.DR
  },
  {
    label: "(DR_D2) Database Access Denied",
    value: ReportType.DR_D2,
    description: "Reports on Access Denied activity for databases where users were denied access because simultaneous-use licenses were exceeded or their institution did not have a license for the database.",
    group: reportGroups.DR
  },
  {
    label: "(PR) Platform Report",
    value: ReportType.PR,
    description: "A customizable report summarizing activity across a content provider’s platforms that allows the user to apply filters and select other configuration options.",
    group: reportGroups.PR
  },
  {
    label: "(PR_P1) Platform Usage",
    value: ReportType.PR_P1,
    description: "Platform-level usage summarized by Metric_Type.",
    group: reportGroups.PR
  },
  {
    label: "(IR) Item Report",
    value: ReportType.IR,
    description: "A granular, customizable report showing activity at the level of the item (article, chapter, media object, etc.) that allows the user to apply filters and select other configuration options.",
    group: reportGroups.IR
  },
  {
    label: "(IR_A1) Journal Article Requests",
    value: ReportType.IR_A1,
    description: "Reports on journal article requests at the article level. This report is limited to content with a Data_Type of Article, Parent_Data_Type of Journal, and Metric_Types of Total_Item_Requests and Unique_Item_Requests. This Standard View must be provided only if (a) it is clear for all articles in IR whether they are journal articles or not and (b) the parent item is known for all journal articles.",
    group: reportGroups.IR
  },
  {
    label: "(IR_M1) Multimedia Item Requests",
    value: ReportType.IR_M1,
    description: "Reports on multimedia requests at the item level.",
    group: reportGroups.IR
  }
];

export const platformReports: Record<string, ReportDefinition[]> = {
  "eref": allReports.filter(x => x.value === "TR_B1" || x.group?.id === "PR"),
  "eref-del": allReports.filter(x => x.value === "TR" || x.value.startsWith("TR_B")  || x.group?.id === "PR"),
  "icare": allReports,
  "medone": allReports.filter(x => x.value === "TR_B1" || x.group?.id === "PR"),
  "medone-tebl": allReports.filter(x => x.value === "TR" || x.value.startsWith("TR_B")  || x.group?.id === "PR"),
  "osteothek": allReports,
  "ps": allReports.filter(x => x.group?.id === "DR" || x.group?.id === "PR"),
  "roempp": allReports.filter(x => x.group?.id !== "TR" && x.value !== "IR_A1"),
  "roempp_sci": allReports.filter(x => x.group?.id !== "TR" && x.value !== "IR_A1"),
  "sos": allReports.filter(x => x.group?.id === "DR" || x.group?.id === "PR"),
  "thieme-connect": allReports.filter(x => x.group?.id !== "DR"),
  "vetcenter": allReports
};
